import { UserDto } from '../../../../auth/user.dto';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { Nationality } from '../../../../shared/models/enums/nationality.enum';
import { ResidentialStatus } from '../../../../shared/models/enums/residential-status.enum';
import { SurveyFSEDto } from '../../fse/surveyFSEDto';
import { AssessmentActivity, AssessmentContext, AssessmentContractTypes, AssessmentHowLongHasWorked, AssessmentJobCenterRegistration, AssessmentStatus, AssessmentSupportProposal, AssessmentUnactivity, AssessmentWorkingTime } from '../assessment.enums';
import { AssessmentAdvantageDto } from './assessment-advantage.dto';
import { AssessmentFormationDto } from './assessment-formation.dto';
import { AssessmentInChargePersonDto } from './assessment-in-charge-person.dto';
import { AssessmentJobSoughtDto } from './assessment-job-sought.dto';
import { AssessmentProExpDto } from './assessment-pro-exp.dto';
import { AssessmentTransportModeDto } from './assessment-transport-mode.dto';

export class AssessmentDto {
  public id: number;
  public beneficiaryId: number;
  public creatorUser: UserDto;
  public creationDateInterview: string;
  public updateStatusDate: string;
  public status: AssessmentStatus;
  public context: AssessmentContext;
  public generatedSurveyFse: SurveyFSEDto;

  // *********************************************************************************************
  // * Detail

  checkedBenefPersonalData: boolean;
  nationality: Nationality;
  validIdentityDocument: boolean;
  renewalInProgress: boolean;
  renewalHelpNeeded: boolean;
  address: string;
  additionalAddress: string;
  city: string;
  postalCode: string;

  singleParent: boolean;

  adminDifficulties: boolean;
  adminBlocking: boolean;
  adminHelpForRights: boolean;
  adminJuridBlocking: boolean;
  adminTaxNotice: boolean;

  readingWritingDifficulties: boolean;
  readingWritingWrittenFrenchDifficulty: boolean;
  readingWritingReadingFrenchDifficulty: boolean;
  readingWritingOralFrenchDifficulty: boolean;
  readingWritingMathDifficulty: boolean;
  readingWritingFrenchAsForeignLanguage: boolean;
  readingWritingLearningDifficulty: boolean;
  readingWritingHasFrenchSupportForProcedures: boolean;
  readingWritingLanguageAssessmentDone: boolean;
  readingWritingLanguageAssessmentDoneDate: Date;
  readingWritingLanguageAssessmentDoneResult: string;
  readingWritingLanguageLevelSufficientForCareer: string;

  inChargePersons: AssessmentInChargePersonDto[] = [];
  childInProgress: boolean;
  isHelper: boolean;
  foreignParents: 'true' | 'false' | 'unknown';

  familyBarriers: boolean;
  familyBarriersDetails: string;
  familyBarriersHasChildUnder3WithoutChildcare: boolean;
  familyBarriersHasChildcareArrangements: boolean;
  familyBarriersHasSearchedForChildcare: boolean;
  familyBarriersHasSearchedForChildcareDetails: string;
  familyBarriersHasChildWithDisability: boolean;
  familyBarriersHasChildWithHealthIssues: boolean;
  familyBarriersHasDependentParent: boolean;
  familyBarriersReceivesSupportForFamilyCare: boolean;
  familyBarriersHasTimeConstraintsForChildPickup: boolean;
  familyBarriersHasTimeConstraintsForChildPickupDetails: string;
  familyBarriersHasOtherFamilyConstraints: boolean;
  familyBarriersWantsParentingAdvice: boolean;

  accreditationSIAE: boolean;
  accreditationSIAEDate: Date;

  accreditationRQTH: string;
  accreditationRQTHDate: Date;

  accreditationAAH: string;
  accreditationAAHDate: Date;

  health: boolean;
  healthCoverageAccessOrUpdate: boolean;
  healthHasHealthCoverage: boolean;
  healthDailyHealthDifficulty: boolean;
  healthLimitsSomeJobs: boolean;
  healthBlocksImmediateWorkReturn: boolean;
  healthCausesRegularAbsences: boolean;
  healthHasMedicalFollowUp: boolean;
  healthMedicalAccess: boolean;
  healthWantsSupportForCareAccess: boolean;
  healthLacksAwarenessOfIssue: boolean;
  healthStrugglesToIntegrateIssueInPath: boolean;

  // *********************************************************************************************
  // * Global situation

  claimRsaInsufRes: boolean;
  claimRsaNoRes: boolean;
  claimRsaNoMoreUnemployRights: boolean;
  claimRsaNoMoreUnemploy: boolean;
  claimRsaRefugeeClaimant: boolean;
  claimRsaOther: boolean;
  claimRsaOtherComment: string;

  financialDifficulties: boolean;
  financialDifficultiesHasOtherResourcesThanRSA: boolean;
  financialDifficultiesAidNeeded: boolean;
  financialDifficultiesAidNeededForRent: boolean;
  financialDifficultiesAidNeededForEnergy: boolean;
  financialDifficultiesAidNeededForFood: boolean;
  financialDifficultiesAidNeededForFines: boolean;
  financialDifficultiesAidNeededForOther: boolean;
  financialDifficultiesHasDebtOrOverindebtedness: boolean;
  financialDifficultiesHasBudgetManagementDifficulty: boolean;
  financialDifficultiesIsBelowPovertyThreshold: boolean;
  financialDifficultiesReceivesThirdPartySupport: boolean;
  financialDifficultiesHasBankingAccessIssue: boolean;

  residentialStatus: ResidentialStatus;
  socialExclusion: boolean;
  indebtedness: boolean;

  housing: boolean;
  housingInadequate: boolean;
  housingUnsanitary: boolean;
  housingEvictionSoon: boolean;
  housingEvictionSoonDetails: string;
  housingSearchInProgress: boolean;
  housingSearchInProgressDetails: string;
  housingSearchWithSupport: boolean;

  autonomousMobility: boolean;
  licenceB: string;
  tclSubscription: string;

  transportModes: AssessmentTransportModeDto[] = [];

  maxMobilityDistance: number;
  maxMobilityGoToWorkTime: number;

  mobility: boolean;
  mobilityDifficulties: boolean;
  mobilityDifficultiesNoTransportAvailable: boolean;
  mobilityDifficultiesDependentOnPublicTransport: boolean;
  mobilityDifficultiesCanRideBike: boolean;
  mobilityDifficultiesOwnBikeOrSharedBike: boolean;
  mobilityDifficultiesNeedsDrivingLicenseForJob: boolean;
  mobilityDifficultiesHasForeignLicenseOutsideEEE: boolean;
  mobilityDifficultiesHealthPreventsDriving: boolean;
  mobilityDifficultiesHealthLimitsOverallMobility: boolean;
  mobilityDifficultiesNeedsAdaptedDrivingLicense: boolean;
  mobilityDifficultiesDifficultyReadingInformation: boolean;
  mobilityDifficultiesStressWhenTraveling: boolean;
  mobilityDifficultiesUnfamiliarWithTCLApps: boolean;
  mobilityDifficultiesPoorSenseOfDirection: boolean;
  mobilityDifficultiesUnawareOfExistingTransportOptions: boolean;

  numericDifficulties: boolean;
  numericDifficultiesHasDevice: boolean;
  numericDifficultiesHasComputer: boolean;
  numericDifficultiesHasSmartphone: boolean;
  numericDifficultiesHasTablet: boolean;
  numericDifficultiesUsesDeviceAlone: boolean;
  numericDifficultiesHasInternetAccess: boolean;
  numericDifficultiesHasEmail: boolean;
  numericDifficultiesHasAccessibleDevice: boolean;
  numericDifficultiesHasFranceConnect: boolean;
  numericDifficultiesHasDigitalID: boolean;
  numericDifficultiesAdministrativeProcessMethod: string;
  numericDifficultiesHasDifficultyWithOnlineProcedures: boolean;
  numericDifficultiesHasPublicAccessToTech: boolean;
  numericDifficultiesKnowsHelpLocations: boolean;

  assistance: boolean;
  assistedByConseiller: boolean;
  assistedByAssociation: boolean;
  assistedByTravailleurSocial: boolean;
  assistedByFamilleAmis: boolean;
  assistedByAutre: boolean;

  socialSatisfaction: boolean;
  socialSelfCareTime: string;
  socialSelfCareActivities: string;
  socialGroupComfort: string;
  socialNewPeopleStress: string;

  isActive: boolean;
  activity: AssessmentActivity;
  unactivity: AssessmentUnactivity;

  // *********************************************************************************************
  // * Orientation

  degree: DisplayLabel;
  studyAbroad: boolean;
  degreeLabel: string;
  degreeGained: boolean;
  trainingAreas: DisplayLabel[];

  formations: AssessmentFormationDto[] = [];

  hasWorked: boolean;
  howLongHasWorked: AssessmentHowLongHasWorked;

  proExps: AssessmentProExpDto[] = [];

  searchingForJob: boolean;
  searchingForJobReasonNotTo: string;
  readyForNewJob: boolean;
  readyForNewJobReasonNotTo: string;
  jobCenterRegistration: AssessmentJobCenterRegistration;
  jobCenterLastUpdateDate: Date;
  integrationStepsTaken: string;

  hasProfessionnalProject: boolean;
  professionnalProjectDetails: string;
  professionalProjectKnowsHowToConductJobSearch: boolean;
  professionalProjectRequiresTraining: boolean;
  professionalProjectCanFollowUpOnTraining: boolean;
  professionalProjectKnowsJobSearchTechniques: boolean;
  professionalProjectHasDefinedCreationProject: boolean;

  accessToFormation: boolean;

  jobSoughts: AssessmentJobSoughtDto[] = [];

  contractSearchType: AssessmentContractTypes;
  workingTime: AssessmentWorkingTime;

  advantages: AssessmentAdvantageDto[] = [];

  expectationsRegardingRSA: string;

  // *********************************************************************************************
  // * Orientation

  supportProposal: AssessmentSupportProposal;
  supportAcceptance: boolean;
  dataSharingAcceptance: boolean;
  supportChoiceExplanation: string;
}
